import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Collapse,
  Avatar,
  LinearProgress,
  Alert,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  TrendingUp,
  CreditCard,
  ExpandMore,
  ExpandLess,
  AutoAwesome,
  Analytics,
  CheckCircle,
  Warning,
} from '@mui/icons-material';
import { LoyaltyProgram, Transaction } from '../../types';
import spendingOptimizationService, {
  CardRecommendation,
  OptimizationAnalysis,
} from '../../services/spendingOptimizationService';

interface SpendingOptimizerProps {
  userCards: LoyaltyProgram[];
  userTransactions?: Transaction[];
}

const OptimizationCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)'
    : 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.1)'}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  },
}));

const CategoryCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    boxShadow: theme.shadows[4],
  },
}));

const ValueChip = styled(Chip)(({ theme }) => ({
  fontWeight: 700,
  borderRadius: 8,
  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  color: 'white',
  '& .MuiChip-label': {
    fontSize: '0.75rem',
  },
}));

const BonusChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  borderRadius: 6,
  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
  color: 'white',
  animation: 'pulse 2s infinite',
  '@keyframes pulse': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.8 },
    '100%': { opacity: 1 },
  },
}));

export const SpendingOptimizer: React.FC<SpendingOptimizerProps> = ({ userCards, userTransactions }) => {
  const [analysis, setAnalysis] = useState<OptimizationAnalysis | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [portfolioRecommendations, setPortfolioRecommendations] = useState<{
    gaps: any[];
    suggestedCards: string[];
    improvements: string[];
  }>({ gaps: [], suggestedCards: [], improvements: [] });

  useEffect(() => {
    const loadAnalysis = async () => {
      if (userCards.length > 0) {
        try {
          const optimizationAnalysis = await spendingOptimizationService.analyzeOptimalCardUsage(
            userCards, 
            userTransactions
          );
          setAnalysis(optimizationAnalysis);

          const recommendations = await spendingOptimizationService.getPortfolioRecommendations(
            userCards,
            userTransactions
          );
          setPortfolioRecommendations(recommendations);
        } catch (error) {
          console.error('Error loading optimization analysis:', error);
        }
      }
    };

    loadAnalysis();
  }, [userCards, userTransactions]);

  const handleCategoryClick = (categoryId: string) => {
    setSelectedCategory(selectedCategory === categoryId ? null : categoryId);
  };

  if (!analysis || analysis.recommendations.length === 0) {
    return (
      <OptimizationCard>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <CreditCard sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Add credit cards to see spending optimization
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Get personalized recommendations for maximizing rewards
          </Typography>
        </CardContent>
      </OptimizationCard>
    );
  }

  return (
    <Box>
      {/* Header Card */}
      <OptimizationCard sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ 
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                width: 48,
                height: 48 
              }}>
                <AutoAwesome />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Smart Card Optimizer
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Maximize rewards across {analysis.recommendations.length} spending categories
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              onClick={() => setExpanded(!expanded)}
              endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
              sx={{ borderRadius: 2 }}
            >
              {expanded ? 'Show Less' : 'View Analysis'}
            </Button>
          </Box>

          {/* Summary Stats */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 2
          }}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: 'success.main' }}>
                {analysis.coverageScore.toFixed(0)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Coverage Score
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main' }}>
                {analysis.recommendations.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Optimized Categories
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: 'warning.main' }}>
                ₹{(analysis.totalOptimizedValue / 100).toFixed(2)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avg Value/Rupee
              </Typography>
            </Box>
          </Box>

          <LinearProgress 
            variant="determinate" 
            value={analysis.coverageScore} 
            sx={{ 
              mt: 2, 
              height: 8, 
              borderRadius: 4,
              backgroundColor: 'rgba(0,0,0,0.1)',
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                borderRadius: 4,
              }
            }} 
          />
        </CardContent>
      </OptimizationCard>

      {/* Detailed Analysis */}
      <Collapse in={expanded}>
        <Box sx={{ mb: 3 }}>
          {/* Category Recommendations */}
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUp color="primary" />
            Optimal Card Usage by Category
          </Typography>

          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 2,
            mb: 3 
          }}>
            {analysis.recommendations.map((recommendation) => (
              <CategoryCard 
                key={recommendation.category.id}
                onClick={() => handleCategoryClick(recommendation.category.id)}
                sx={{
                  borderColor: selectedCategory === recommendation.category.id ? 'primary.main' : 'divider'
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
                        {recommendation.category.icon}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                        {recommendation.category.name}
                      </Typography>
                    </Box>
                    {recommendation.quarterlyBonus?.isActive && (
                      <BonusChip 
                        label="BONUS" 
                        size="small"
                      />
                    )}
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.75rem' }}>
                    {recommendation.card.name}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                      {recommendation.reason}
                    </Typography>
                    <ValueChip 
                      label={`₹${(recommendation.valuePerDollar / 100).toFixed(2)}`}
                      size="small"
                    />
                  </Box>

                  <Collapse in={selectedCategory === recommendation.category.id}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      {recommendation.category.description}
                    </Typography>
                    {recommendation.quarterlyBonus?.isActive && (
                      <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
                        <Typography variant="caption">
                          {recommendation.quarterlyBonus.description}
                        </Typography>
                      </Alert>
                    )}
                  </Collapse>
                </CardContent>
              </CategoryCard>
            ))}
          </Box>

          {/* Portfolio Recommendations */}
          {portfolioRecommendations.gaps.length > 0 && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Analytics color="warning" />
                Portfolio Improvement Opportunities
              </Typography>

              <Card sx={{ mb: 2, borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Warning color="warning" fontSize="small" />
                    Categories with Limited Rewards
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {portfolioRecommendations.gaps.map((gap) => (
                      <Chip
                        key={gap.id}
                        label={`${gap.icon} ${gap.name}`}
                        variant="outlined"
                        size="small"
                        color="warning"
                      />
                    ))}
                  </Box>

                  {portfolioRecommendations.suggestedCards.length > 0 && (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        Suggested Cards to Consider:
                      </Typography>
                      {portfolioRecommendations.suggestedCards.map((suggestion, index) => (
                        <Typography key={index} variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                          • {suggestion}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Success Message */}
          {analysis.coverageScore >= 80 && (
            <Alert 
              severity="success" 
              sx={{ borderRadius: 3 }}
              icon={<CheckCircle />}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Excellent card portfolio! You have optimal coverage for most spending categories.
              </Typography>
            </Alert>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default SpendingOptimizer;